import { createBrowserLocalStorageCache } from '@algolia/cache-browser-local-storage';
import { ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY, AlgoliaIndex } from '@core/algolia';
import algoliasearch, { SearchIndex } from 'algoliasearch';
import { SearchOptions } from 'instantsearch.js';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';

const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY, {
  responsesCache: createBrowserLocalStorageCache({ key: `algolia-responses-${ALGOLIA_APP_ID}`, timeToLive: 3600 }),
});

export interface AlgoliaResponse<T> {
  queryID?: string;
  nbHits: number;
  nbPages: number;
  page: number;
  hitsPerPage: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderingContent?: any;
  hits: T[];
  facets?: Record<string, Record<string, number>>;
}

interface UseAlgoliaQueryReturn<T> {
  index: AlgoliaIndex;
  response: AlgoliaResponse<T>;
  isLoading: boolean;
}

const emptyResponse: AlgoliaResponse<unknown> = {
  // exhaustiveNbHits: false,
  queryID: '',
  facets: {},
  hits: [],
  nbHits: 0,
  // processingTimeMS: 0,
  hitsPerPage: 0,
  nbPages: 0,
  page: 0,
  // params: '',
  // query: '',
};

export default function <T>(
  indexName: AlgoliaIndex,
  query: string,
  searchOptions: SearchOptions,
  debounceInterval = 375,
  articleNumberDebounceInterval = 2000,
  minimumSearchCharacters = 3,
  enabled = true,
): UseAlgoliaQueryReturn<T> {
  const index = client.initIndex(indexName);
  const [response, setResponse] = useState<AlgoliaResponse<T>>({ ...(emptyResponse as AlgoliaResponse<T>) });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const doSearch = async (
    i: SearchIndex,
    q: string,
    sO: SearchOptions,
    type: 'all' | 'article-number',
  ): Promise<void> => {
    switch (type) {
      case 'all': {
        debouncedArticleNumberSearch.cancel();
        break;
      }

      case 'article-number': {
        debouncedSearch.cancel();
        break;
      }
    }

    let filters;

    if (indexName.indexOf('products') > -1 && (!sO || !sO.filters || sO.filters.length === 0)) {
      filters = `isPublished:"true"`;
    } else {
      filters = sO.filters;
    }

    const _response = await i.search<T>(q, { ...sO, filters });

    setResponse({ ..._response });
    setIsLoading(false);
  };

  const debouncedSearch = useCallback(
    debounce(async (i: SearchIndex, q: string, sO: SearchOptions) => doSearch(i, q, sO, 'all'), debounceInterval),
    [],
  );

  const debouncedArticleNumberSearch = useCallback(
    debounce(
      async (i: SearchIndex, q: string, sO: SearchOptions) => doSearch(i, q, sO, 'article-number'),
      articleNumberDebounceInterval,
    ),
    [],
  );

  useEffect(() => {
    if (query.length < minimumSearchCharacters || !enabled) {
      setResponse({ ...(emptyResponse as AlgoliaResponse<T>) });
      setIsLoading(false);
      return;
    }

    // Adding some rules to prevent unnecessary requests
    // If the query starts with a 4 and is only number we are going to assume it is an article number search
    // If it's an article number search we only want to search in the products index
    if (query.match(/^4(\d+)?$/) !== null) {
      if (!indexName.startsWith('products')) {
        return;
      }

      setIsLoading(true);
      debouncedArticleNumberSearch(index, query, searchOptions);
    } else {
      setIsLoading(true);
      debouncedSearch(index, query, searchOptions);
    }
  }, [indexName, query, JSON.stringify(searchOptions)]);

  return { response, isLoading, index: indexName };
}

export function buildFilterQuery(filters: Record<string, string[] | undefined>, isPublished: boolean): string {
  const querySections = [];

  for (const codeName of Object.keys(filters)) {
    const values = filters[codeName];

    if (values && values.length > 0) {
      const valueQuery = values.map((value) => `${codeName}:"${value}"`).join(' OR ');
      querySections.push(`(${valueQuery})`);
    }
  }

  querySections.push(`isPublished:"${isPublished}"`);

  let filterQuery = '';

  if (querySections.length > 1) {
    filterQuery = querySections.join(' AND ');
  } else {
    filterQuery = querySections.join('');
  }

  return filterQuery;

  // return `(isPublished:${isPublished}) AND (${filterQuery})`;
}
